import React, { memo, useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Alert, AlertTitle, Button, Chip, IconButton, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import InputBase from '@mui/material/InputBase';

import { useLanguage, useProjectConfig } from '../../hooks';
import { DatascopeDictionary, IntegerDictionary, QuestionInterface } from '../../types/oldWebForms';
import { ConfirmActionDialog } from '../uiComponents/index';
import colors from './colors';
import { iconTypes } from './iconTypes';
import { questionTypesList } from './questionTypes';
import { SubformNavigator } from './subformNavigator';

const cacheIds: IntegerDictionary<string> = {};
export const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 30,
  keyMapper: (index) => {
    return cacheIds[index];
  },
});

export const NavigatorItem = memo((props: any) => {
  const {
    callbackSetAdapter,
    dropdownState,
    id,
    index,
    isHovered,
    isZoomed,
    name,
    onHoveredHandler,
    onNameChange,
    onNameInSectionChange,
    onNameInTableChange,
    onScrolledIdxHandler,
    onZoomedHandler,
    onZoomedInTableHandler,
    sectionSubformIndexes,
    setConfirmEmptyOpen,
    setDropdownState,
    setInitialState,
    subformId,
    subformIndex,
    subQuestionRemove,
    subQuestions,
    type,
  } = props;

  const [isRenamed, setIsRenamed] = useState(false);
  const [endIconHover, setEndIconHover] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const { blockFormBuilder } = useProjectConfig();

  const toggleEndIconHover = () => setEndIconHover(!endIconHover);

  useEffect(() => {
    if (subformIndex >= 0) {
      cache.clear(subformIndex, 0);
      callbackSetAdapter();
    }
  }, [subQuestions]);

  useEffect(() => {
    if (type === 'section' && sectionSubformIndexes && sectionSubformIndexes.length !== 0) {
      sectionSubformIndexes.forEach((subformIndex: number) => {
        cache.clear(subformIndex, 0);
        callbackSetAdapter();
      });
    }
  }, [dropdownState]);

  const updateStates = () => {
    setDropdownState((prevState: any) => {
      const current = prevState[id];
      const newState = { ...prevState };
      newState[id] = !current;
      return newState;
    });
    if (type !== 'section') setInitialState(false);
  };

  const handleSubformDelete = () => {
    subQuestionRemove(subformId, index, id);
    setConfirmRemoveOpen(false);
  };

  const collapseDropdown = () => {
    updateStates();
    cache.clear(index, 0);
    callbackSetAdapter();
  };

  const styles: DatascopeDictionary<React.CSSProperties> = {
    itemContainer: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 13,
      width: 248,
      height: 28,
      cursor: 'pointer',
    },
    iconContainer: {
      margin:
        type === 'section'
          ? '0 3px 0'
          : subQuestions && type !== 'table'
          ? '0 8px 0 38px'
          : '0 8px 0 23px',
    },
    nameContainer: {
      width: 203,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    },
    endIconContainer: {
      width: 30,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'right',
    },
    endIcon: {
      border: endIconHover ? '1px solid black' : null,
      borderRadius: '3px',
      borderColor: endIconHover ? colors.blue : null,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    chip: {
      height: 18,
    },
    removeIcon: { fontSize: 18 },
  };

  const lang = useLanguage();
  return (
    <div
      style={{
        ...styles.itemContainer,
        backgroundColor: isZoomed ? colors.lightSecondary : colors.trans,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: isHovered ? colors.primary : colors.trans,
      }}
      onClick={() => {
        if (subQuestions && type !== 'table' && subformId) {
          onZoomedInTableHandler(id);
          onScrolledIdxHandler(subformIndex);
          onZoomedHandler(subformId);
        } else {
          if (type !== 'section') onZoomedHandler(id);
          onScrolledIdxHandler(index);
        }
      }}
      onMouseEnter={() => onHoveredHandler(id)}
      onMouseLeave={() => onHoveredHandler(undefined)}
    >
      <ConfirmActionDialog
        isOpen={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        warningTitle={lang.form_builder.areYouSure}
        warningMessage={lang.form_builder.subformRemoveConfirm}
        onConfirmAction={handleSubformDelete}
        confirmActionButtonText={lang.form_builder.confirm}
      />
      <div style={styles.iconContainer}>
        {type === 'section'
          ? null
          : iconTypes(12, 0, colors.primary)[
              questionTypesList.includes(type) ? type : 'deprecated'
            ]}
      </div>
      {isRenamed ? (
        <InputBase
          fullWidth
          autoFocus
          value={name}
          onChange={(e) => {
            if (type !== 'section') {
              if (subQuestions && type !== 'table') {
                onNameInTableChange(id, e.target.value, subformId);
              } else {
                onNameChange(id, e.target.value);
              }
            } else onNameInSectionChange(id, e.target.value);
          }}
          onBlur={() => {
            if (name === '' && type === 'section')
              onNameInSectionChange(id, lang.form_builder.sectionName);

            setIsRenamed(false);
          }}
          style={{ fontSize: 13, textAlign: 'center' }}
          inputProps={{
            style: {
              borderRadius: 3,
              backgroundColor: 'white',
              padding: 1,
            },
          }}
        />
      ) : (
        <div
          onDoubleClick={blockFormBuilder ? null : () => setIsRenamed(true)}
          style={styles.nameContainer}
        >
          {name}
        </div>
      )}
      {subQuestions && type !== 'table' && isHovered && (
        <Tooltip title={lang.form_builder.removeSubformQuestionTooltip}>
          <IconButton
            disabled={blockFormBuilder}
            onClick={() => {
              setConfirmRemoveOpen(true);
              cache.clear(subformIndex, 0);
              callbackSetAdapter();
            }}
            size='small'
            color={blockFormBuilder ? 'inherit' : 'warning'}
            style={{ height: 18, width: 18 }}
          >
            <RemoveIcon
              style={styles.removeIcon}
              color={'inherit'}
            />
          </IconButton>
        </Tooltip>
      )}
      {type === 'table' && dropdownState && dropdownState[id] && isHovered && (
        <Tooltip title={lang.form_builder.emptySubformTooltip}>
          <Chip
            label={`${lang.form_builder.emptySubform}`}
            disabled={blockFormBuilder}
            onClick={() => {
              setConfirmEmptyOpen(true);
              cache.clear(index, 0);
              callbackSetAdapter();
            }}
            style={{ color: '#ff0055', height: 18, marginRight: 4 }}
          />
        </Tooltip>
      )}
      {type === 'table' && dropdownState && !dropdownState[id] && isHovered && (
        <Chip
          label={`${subQuestions.length}`}
          style={{ height: 18 }}
        />
      )}
      {(type === 'table' || type === 'section') && isHovered && (
        <div style={styles.endIconContainer}>
          <div
            style={styles.endIcon}
            onClick={collapseDropdown}
            onMouseEnter={toggleEndIconHover}
            onMouseLeave={toggleEndIconHover}
          >
            {iconTypes(12, 0, colors.blue)['section']}
          </div>
        </div>
      )}
    </div>
  );
});

const NavigatorRow: any = (props: any) => {
  const { blockFormBuilder } = useProjectConfig();
  return ({ index, style, parent }: any) => {
    const {
      callbackSetAdapter,
      hoveredId,
      initialState,
      onHoveredHandler,
      onNameChange,
      onNameInSectionChange,
      onNameInTableChange,
      onScrolledIdxHandler,
      onZoomedHandler,
      onZoomedInTableHandler,
      questions,
      sectionStates,
      setInitialState,
      setSectionStates,
      setSubformStates,
      subformEmpty,
      subformStates,
      subQuestionRemove,
      zoomedId,
    } = props;
    const question = questions[index];
    let sectionSubformIndexes: number[];
    if (question.type === 'section') {
      sectionSubformIndexes = subformStates[question.id];
    }
    let totalQuestions: any[] = [];
    questions.forEach((q: any) => {
      totalQuestions.push(q);
      if (q.type === 'table') {
        totalQuestions = totalQuestions.concat(q.questions);
      }
    });

    if (!question) return null;

    const { id, name, type, sectionId } = question;
    const isZoomed = id === zoomedId;
    const isHovered = id === hoveredId;
    const subQuestions = question.questions;
    // If sectionId is undefined either no sections have been defined, or the question is a section.
    // In both cases we want to show the question.
    if (sectionStates[sectionId] || !sectionId) {
      return (
        <div
          style={style}
          key={id}
        >
          <CellMeasurer
            cache={cache}
            columnIndex={0}
            parent={parent}
            rowIndex={index}
            key={id}
          >
            <div>
              <Draggable
                key={id}
                draggableId={id}
                index={index}
                isDragDisabled={question.type === 'section' || blockFormBuilder}
              >
                {(provided, snapshot) => (
                  <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                  >
                    {question.type === 'table' ? (
                      <SubformNavigator
                        callbackSetAdapter={callbackSetAdapter}
                        hoveredId={hoveredId}
                        index={index}
                        initialState={initialState}
                        isDragging={snapshot.isDragging}
                        key={id}
                        onHoveredHandler={onHoveredHandler}
                        onNameChange={onNameChange}
                        onNameInSectionChange={onNameInSectionChange}
                        onNameInTableChange={onNameInTableChange}
                        onScrolledIdxHandler={onScrolledIdxHandler}
                        onZoomedHandler={onZoomedHandler}
                        onZoomedInTableHandler={onZoomedInTableHandler}
                        sectionOpen={sectionStates[id]}
                        setInitialState={setInitialState}
                        setSubformStates={setSubformStates}
                        subformEmpty={subformEmpty}
                        subformId={id}
                        subformName={name}
                        subformStates={subformStates}
                        subQuestionRemove={subQuestionRemove}
                        subQuestions={subQuestions}
                        zoomedId={zoomedId}
                      />
                    ) : (
                      <NavigatorItem
                        key={id}
                        index={index}
                        id={id}
                        name={name}
                        type={type}
                        isZoomed={isZoomed}
                        isHovered={isHovered}
                        onZoomedHandler={onZoomedHandler}
                        onHoveredHandler={onHoveredHandler}
                        onScrolledIdxHandler={onScrolledIdxHandler}
                        onNameChange={onNameChange}
                        onNameInSectionChange={onNameInSectionChange}
                        subQuestions={subQuestions}
                        isDragging={snapshot.isDragging}
                        callbackSetAdapter={callbackSetAdapter}
                        sectionOpen={type !== 'section' ? sectionStates[id] : null}
                        dropdownState={type === 'section' ? sectionStates : null}
                        setDropdownState={type === 'section' ? setSectionStates : null}
                        initialState={initialState}
                        setInitialState={setInitialState}
                        sectionSubformIndexes={sectionSubformIndexes}
                      />
                    )}
                  </div>
                )}
              </Draggable>
            </div>
          </CellMeasurer>
        </div>
      );
    }
  };
};

const QuestionNavigator = memo((props: any) => {
  const {
    combineAlert,
    combineAlertText,
    emptySubformAlert,
    hoveredId,
    onAddHandler,
    onDragEndHandler,
    onHoveredHandler,
    onNameChange,
    onNameInTableChange,
    onScrolledIdxHandler,
    onZoomedHandler,
    onZoomedInTableHandler,
    sectionStates,
    setCombineAlert,
    setEmptySubformAlert,
    setSectionStates,
    setSubformStates,
    subformEmpty,
    subformStates,
    subQuestionRemove,
    windowHeight,
    zoomedId,
  } = props;
  const questions: QuestionInterface[] =
    Object.keys(sectionStates).length !== 0
      ? props.questions.filter((q: any) => q.type === 'section' || sectionStates[q.sectionId])
      : props.questions;
  const styles: DatascopeDictionary<React.CSSProperties> = {
    mainContainer: {
      display: 'flex',
      margin: '7px 0 0',
    },
    alert: {
      position: 'absolute',
      bottom: 35,
      left: 10,
      height: 'auto',
      maxWidth: 183,
    },
    alertTitle: {
      fontWeight: 'bold',
    },
    addButton: { borderRadius: 0 },
  };
  const lang = useLanguage();
  const { blockFormBuilder } = useProjectConfig();

  const [initialState, setInitialState] = useState(true);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setAdapter] = useState(true);
  const callbackSetAdapter = useCallback(() => {
    setAdapter((prev) => !prev);
  }, []);
  const renderCloneCallback = useCallback(
    // @ts-ignore
    (provided, snapshot, rubric) => {
      const index = rubric.source.index;
      const { id, name, type } = questions[index];
      const isZoomed = id === zoomedId;
      const isHovered = id === hoveredId;
      const subQuestions = questions[index].questions;
      return (
        <div
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <NavigatorItem
            key={id}
            index={index}
            id={id}
            name={name}
            type={type}
            isZoomed={isZoomed}
            isHovered={isHovered}
            onZoomedHandler={onZoomedHandler}
            onHoveredHandler={onHoveredHandler}
            onScrolledIdxHandler={onScrolledIdxHandler}
            onNameChange={onNameChange}
            onNameInTableChange={onNameInTableChange}
            subQuestions={subQuestions}
            isDragging={snapshot.isDragging}
            onZoomedInTableHandler={onZoomedInTableHandler}
          />
        </div>
      );
    },
    [
      questions,
      zoomedId,
      hoveredId,
      onZoomedHandler,
      onHoveredHandler,
      onScrolledIdxHandler,
      onNameChange,
      onNameInTableChange,
      onZoomedInTableHandler,
    ],
  );
  useEffect(() => {
    questions.forEach((q, index) => {
      if (cacheIds[index] && cacheIds[index] !== q.id) {
        // The column index is always 0 because we only have one column.
        cache.clear(index, 0);
      }
      cacheIds[index] = q.id;
    });
  }, [questions]);

  return (
    <div style={styles.mainContainer}>
      <Card>
        <DragDropContext onDragEnd={(event) => onDragEndHandler(event, 'navigator')}>
          <Droppable
            isCombineEnabled
            droppableId={'navigatorDroppable'}
            type={'navigator'}
            mode={'virtual'}
            renderClone={renderCloneCallback}
          >
            {(provided) => (
              // FIXME: search a better way to use this function
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <List
                  height={windowHeight - 115}
                  rowCount={questions.length}
                  rowHeight={initialState ? 30 : cache.rowHeight}
                  width={250}
                  ref={(ref) => {
                    // This allow scrolling while dragging an item
                    if (ref) {
                      // eslint-disable-next-line react/no-find-dom-node
                      const listRef = ReactDOM.findDOMNode(ref);
                      if (listRef instanceof HTMLElement) {
                        provided.innerRef(listRef);
                      }
                    }
                  }}
                  rowRenderer={NavigatorRow({
                    ...props,
                    questions,
                    callbackSetAdapter,
                    sectionStates,
                    setSectionStates,
                    subQuestionRemove,
                    subformEmpty,
                    initialState,
                    setInitialState,
                    subformStates,
                    setSubformStates,
                  })}
                  overscanRowCount={0}
                />
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {emptySubformAlert && (
          <Alert
            icon={false}
            onClose={() => setEmptySubformAlert(!emptySubformAlert)}
            style={styles.alert}
            severity='warning'
          >
            <AlertTitle style={styles.alertTitle}>
              {lang.form_builder.emptySubformAlertTitle}
            </AlertTitle>
            {lang.form_builder.emptySubformAlert}
          </Alert>
        )}
        {combineAlert && (
          <Alert
            icon={false}
            onClose={() => setCombineAlert(!combineAlert)}
            style={styles.alert}
            severity='error'
          >
            <AlertTitle style={styles.alertTitle}>{lang.form_builder.combineAlertTitle}</AlertTitle>
            {combineAlertText}
          </Alert>
        )}
        <Button
          startIcon={<AddIcon />}
          variant='contained'
          sx={styles.addButton}
          fullWidth
          disabled={blockFormBuilder}
          size='small'
          onClick={() => {
            onAddHandler(questions.length);
            callbackSetAdapter();
          }}
        >
          {lang.form_builder.add}
        </Button>
      </Card>
    </div>
  );
});

export { QuestionNavigator };
